import React from "react";
import { Layout, Table, Checkbox, Typography } from "antd";
import type { ColumnsType, TablePaginationConfig } from "antd/es/table";
import { CheckboxValueType } from "antd/es/checkbox/Group";

import { BitmakerAPIClient } from "../../services/BitmakerApp";
import {
  AuditBMUser,
  AuditBMUserFromJSON,
} from "../../bitmaker/schemas/BMUser";
import { fromDatetimeToString } from "../../utils";

const { Content } = Layout;
const { Title } = Typography;

interface Props {}

interface State {
  auditBMUsers: Array<AuditBMUser>;
  currentPage: number;
  pageSize: number;
  totalItems: number;
  loading: boolean;
  selectedFields: Array<string>;
}

export class AuditBMUsers extends React.PureComponent<Props, State> {
  auditBMUsersColumns: ColumnsType<AuditBMUser> = [
    {
      key: "audit_id",
      title: "Audit ID",
      dataIndex: "audit_id",
      align: "center",
      width: "5%",
    },
    {
      key: "operation_type",
      title: "Operation type",
      dataIndex: "operation_type",
      align: "center",
      width: "10%",
    },
    {
      key: "target_user_id",
      title: "User ID",
      dataIndex: "target_user_id",
      align: "center",
      width: "5%",
    },
    {
      key: "target_user_login",
      title: "Redmine Login",
      dataIndex: "target_user_login",
      align: "center",
      width: "15%",
    },
    {
      key: "changes",
      title: "Changes",
      dataIndex: "changes",
      width: "35%",
      render: (_, record) => {
        let changes = getChanges(record.old_values, record.new_values);
        return (
          <ul>
            {changes.map((change, index) => (
              <li key={index}>{change}</li>
            ))}
          </ul>
        );
      },
    },
    {
      key: "audit_date",
      title: "Audit date",
      dataIndex: "audit_date",
      align: "center",
      width: "15%",
      render: (_, record) => (
        <span>{fromDatetimeToString(record.audit_date)}</span>
      ),
    },
    {
      key: "actor_username",
      title: "Changed by",
      dataIndex: "actor_username",
      align: "center",
      width: "15%",
    },
  ];

  constructor(props: Props) {
    super(props);
    this.state = {
      auditBMUsers: [],
      currentPage: 1,
      pageSize: 10,
      totalItems: 0,
      loading: false,
      selectedFields: [],
    };
  }

  public componentDidMount() {
    this.fetchAuditBMUsers();
  }

  handleFilterChange = (checkedValues: CheckboxValueType[]) => {
    const selectedFields = checkedValues.map((value) => value.toString());

    this.setState({ selectedFields }, () => {
      this.fetchAuditBMUsers();
    });
  };

  fetchAuditBMUsers = () => {
    const { currentPage, pageSize, selectedFields } = this.state;

    this.setState({ loading: true });

    const handleUsersResponse = (response: any) => {
      const auditBMUsers = (response.data.data as Array<AuditBMUser>).map(
        AuditBMUserFromJSON,
      );

      this.setState({
        auditBMUsers,
        totalItems: response.data.total_items,
        loading: false,
      });
    };

    BitmakerAPIClient.get(
      `/audit_bmusers`,
      {
        page: currentPage,
        per_page: pageSize,
        changed_field: selectedFields,
      },
      handleUsersResponse,
    );
  };

  handleTableChange = (pagination: TablePaginationConfig) => {
    this.setState(
      {
        currentPage: pagination.current || 1,
        pageSize: pagination.pageSize || 10,
      },
      () => {
        this.fetchAuditBMUsers();
      },
    );
  };

  public render() {
    const {
      auditBMUsers,
      currentPage,
      pageSize,
      totalItems,
      loading,
      selectedFields,
    } = this.state;

    return (
      <Layout>
        <Content>
          <div className="table-section">
            <div className="table-title">
              <Title level={5}>Audit BMUsers</Title>
            </div>

            <Checkbox.Group
              options={[
                { label: "Availability", value: "availability" },
                { label: "PTO", value: "pto" },
                { label: "Redmine Login", value: "redmine_login" },
                { label: "Fulltime", value: "fulltime" },
              ]}
              value={selectedFields}
              onChange={this.handleFilterChange}
              style={{ marginBottom: 16 }}
            />
            <div className="table-scroll">
              <Table
                columns={this.auditBMUsersColumns}
                dataSource={auditBMUsers}
                size={"large"}
                pagination={{
                  current: currentPage,
                  pageSize: pageSize,
                  total: totalItems,
                  showSizeChanger: true,
                }}
                loading={loading}
                onChange={this.handleTableChange}
                bordered
                rowKey={(record) => record.audit_id}
              />
            </div>
          </div>
        </Content>
      </Layout>
    );
  }
}

function getChanges(
  oldValues: Record<string, any> | null,
  newValues: Record<string, any> | null,
): React.ReactNode[] {
  const columns = [
    "pto",
    "redmine_login",
    "github_user",
    "display_name",
    "slack_id",
    "fulltime",
    "start_date",
    "availability",
    "redmine_id",
  ];

  let changes: React.ReactNode[] = [];

  columns.forEach((column, index) => {
    const oldValue = oldValues ? oldValues[column] : null;
    const newValue = newValues ? newValues[column] : null;

    if (oldValue !== newValue) {
      const changeDescription =
        oldValue === null || oldValue === undefined ? (
          <span key={index}>
            '{column}' was added with value '{String(newValue)}'
          </span>
        ) : newValue === null || newValue === undefined ? (
          <span key={index}>
            '{column}' was removed (was '{String(oldValue)}')
          </span>
        ) : (
          <span key={index}>
            '{column}' changed from '{String(oldValue)}' to '{String(newValue)}'
          </span>
        );

      changes.push(changeDescription);
    }
  });

  return changes;
}
